/**
 * Formats a date-only or timestamp value for Belgian display (`dd/MM/yyyy`).
 *
 * Plain ISO `yyyy-MM-dd` values are reformatted directly (no timezone
 * conversion, since they carry no time component). Timestamps are
 * interpreted as UTC and converted to the `Europe/Brussels` timezone.
 *
 * @param value - An ISO date (`yyyy-MM-dd`) or timestamp string, or `null`/`undefined`.
 * @returns The date formatted as `dd/MM/yyyy`, the original string if unparsable, or `''` if empty.
 */
export function formatBelgianDate(value?: string | null): string {
  if (!value) {
    return '';
  }

  const isoMatch = value.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (isoMatch) {
    return `${isoMatch[3]}/${isoMatch[2]}/${isoMatch[1]}`;
  }

  const date = parseUtcTimestamp(value);
  if (Number.isNaN(date.getTime())) {
    return value;
  }

  return new Intl.DateTimeFormat('nl-BE', {
    timeZone: 'Europe/Brussels', day: '2-digit', month: '2-digit', year: 'numeric',
  }).format(date);
}

/**
 * Formats a timestamp for Belgian display, including the time
 * (`dd/MM/yyyy HH:mm`, 24-hour clock, `Europe/Brussels` timezone).
 *
 * @param value - An ISO timestamp string, or `null`/`undefined`.
 * @returns The formatted date-time, the original string if unparsable, or `''` if empty.
 */
export function formatBelgianDateTime(value?: string | null): string {
  if (!value) {
    return '';
  }

  const date = parseUtcTimestamp(value);
  if (Number.isNaN(date.getTime())) {
    return value;
  }

  return new Intl.DateTimeFormat('nl-BE', {
    timeZone: 'Europe/Brussels', day: '2-digit', month: '2-digit', year: 'numeric',
    hour: '2-digit', minute: '2-digit', hour12: false,
  }).format(date).replace(',', '');
}

/**
 * Parses a timestamp string as UTC when it has no explicit timezone offset.
 *
 * The API returns unambiguous ISO timestamps, but some are naive (no `Z` or
 * offset suffix); without correction `new Date(...)` would interpret those
 * as local time instead of UTC.
 *
 * @param value - The timestamp string to parse.
 * @returns The parsed `Date`, forced to UTC when no explicit zone was present.
 */
function parseUtcTimestamp(value: string): Date {
  const hasExplicitZone = /(?:Z|[+-]\d{2}:?\d{2})$/i.test(value);
  const timestamp = value.includes('T') && !hasExplicitZone ? `${value}Z` : value;
  return new Date(timestamp);
}

/**
 * Parses a Belgian-format date string (`dd/MM/yyyy`) into an ISO date.
 *
 * Validates that the date actually exists (e.g. rejects `31/02/2024`) by
 * checking the constructed date's components round-trip correctly.
 *
 * @param value - The date string in `dd/MM/yyyy` format.
 * @returns The date as an ISO `yyyy-MM-dd` string, or `null` if the format or date is invalid.
 */
export function parseBelgianDate(value: string): string | null {
  const match = value.match(/^(\d{2})\/(\d{2})\/(\d{4})$/);
  if (!match) return null;

  const day = Number(match[1]);
  const month = Number(match[2]);
  const year = Number(match[3]);
  const date = new Date(0);
  date.setUTCHours(0, 0, 0, 0);
  date.setUTCFullYear(year, month - 1, day);
  if (date.getUTCFullYear() !== year || date.getUTCMonth() !== month - 1 || date.getUTCDate() !== day) {
    return null;
  }

  return `${match[3]}-${match[2]}-${match[1]}`;
}

/**
 * Progressively inserts `/` separators into raw digit input as the user
 * types a Belgian-format date, without requiring them to type the slashes.
 *
 * @param value - The raw input string (may already contain partial formatting).
 * @returns The input reformatted as up to `dd/MM/yyyy`, truncated to 8 digits.
 */
export function normalizeBelgianDateInput(value: string): string {
  const digits = value.replace(/\D/g, '').slice(0, 8);
  if (digits.length <= 2) return digits;
  if (digits.length <= 4) return `${digits.slice(0, 2)}/${digits.slice(2)}`;
  return `${digits.slice(0, 2)}/${digits.slice(2, 4)}/${digits.slice(4)}`;
}
